import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import { cn } from '@/utils/cn'
import React, { HTMLAttributes } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  XAxis,
  YAxis,
} from 'recharts'

import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { formatCurrency } from '@/shared/lib/utils/format-currency'

export const CHART_CONFIG: ChartConfig = {
  priceHigh: {
    label: '최고가',
    color: '#C9C9C9',
  },
  priceLow: {
    label: '최저가',
    color: '#FF6400',
  },
  priceAvg: {
    label: '평균가',
    color: '#0064FF',
  },
}
type History = {
  id: string
  price: number
  priceHigh: number
  priceLow: number
  currentDate: string
}
interface HistoryChartComponentProps extends HTMLAttributes<HTMLDivElement> {
  height: number
  isDataEmpty?: boolean
  chartConfig: ChartConfig
  histories: History[]
  lowestData: History
  highestData: History
}

export const HistoryChartComponent = ({
  className,
  height,
  isDataEmpty,
  chartConfig,
  histories,
  highestData,
  lowestData,
  ...props
}: HistoryChartComponentProps) => {
  return (
    <div className={cn(className, 'relative size-auto')} {...props}>
      {isDataEmpty && <EmptyDataCard />}
      <ChartContainer
        config={chartConfig}
        // eslint-disable-next-line tailwindcss/classnames-order, tailwindcss/no-custom-classname
        className={`h-[${height}px] w-full bg-white ${isDataEmpty ? 'brightness-75' : ''} px-1 sm:px-0`}
      >
        <LineChart accessibilityLayer data={histories}>
          <CartesianGrid horizontal={false} vertical={false} />
          <XAxis
            dataKey="id"
            axisLine={false}
            tick={false}
            padding={{ left: 35, right: 35 }}
          />
          {!isDataEmpty && (
            <ChartTooltip
              position={{ y: -0 }}
              content={
                <ChartTooltipContent
                  labelFormatter={(value) => {
                    const currentDate = histories.find(
                      (ele) => ele.id === value,
                    )?.currentDate
                    return currentDate?.split('T')[0] ?? ''
                  }}
                />
              }
            />
          )}

          <YAxis
            hide
            type="number"
            domain={[
              (dataMin: number) => Math.min(dataMin, lowestData.priceLow),
              (dataMax: number) => Math.max(dataMax, highestData.priceHigh),
            ]}
            padding={{ top: 30 }}
          />
          <Line
            strokeWidth={2}
            dataKey="priceHigh"
            fill={chartConfig.priceHigh.color}
            dot={false}
            stroke={chartConfig.priceHigh.color}
          />
          <Line
            strokeWidth={2}
            dataKey="priceLow"
            fill={chartConfig.priceLow.color}
            dot={false}
            stroke={chartConfig.priceLow.color}
          />
          <ReferenceDot
            x={highestData.id}
            y={highestData.priceHigh}
            fill={chartConfig.priceHigh.color}
            r={4}
            label={{
              style: {
                fill: chartConfig.priceHigh.color,
                fontSize: '10px',
                fontWeight: 500,
                transform: 'translateY(-10px)',
              },
              value: `최고가 ${formatCurrency(highestData.priceHigh)}`,
            }}
          />
          <ReferenceDot
            x={lowestData.id}
            y={lowestData.priceLow}
            fill={chartConfig.priceLow.color}
            r={4}
            label={{
              style: {
                fill: chartConfig.priceLow.color,
                fontSize: '10px',
                fontWeight: 500,
                transform: 'translateY(10px)',
              },
              value: `최저가 ${formatCurrency(lowestData.priceLow)}`,
            }}
          />

          {/* <ReferenceLine
            y={priceAvg}
            strokeWidth={1}
            label={{
              value: '평균가',
              fill: chartConfig.priceAvg.color,
              position: 'insideBottomLeft',
            }}
            fill={chartConfig.priceAvg.color}
            stroke={chartConfig.priceAvg.color}
            strokeDasharray="5 5"
          /> */}
        </LineChart>
      </ChartContainer>
    </div>
  )
}

const EmptyDataCard = () => {
  return (
    <Card className="absolute left-1/2 top-1/2 z-10 w-2/3 max-w-[400px] -translate-x-1/2 -translate-y-1/2 bg-white p-3 text-center">
      <CardHeader className="p-2" />
      <CardContent className="w-full">
        <p>가격 데이터가 없는 상품입니다.</p>
        <p>알림을 설정하면 가격 추적을 시작해요.</p>
      </CardContent>
    </Card>
  )
}
